import { Box, IconButton, Grid, Card, CardMedia, Pagination } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import {
  getMedia,
  deleteMedia,
  getMediaStatus,
  updateStatus,
  getMediasError,
  getMediaSuccess,
  selectAllMedias,
  selectCurrentPage,
  selectTotalCount,
} from "../../features/mediaSlice";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import Loading from "../../components/loading";
import NoData from "../../components/noData";
import AlertDialog from "../../components/alertDialog";
import BackButton from "../../components/backButton";
import DeleteIcon from "@mui/icons-material/Delete";
import RestaurantAppBar from "../../components/restaurantTopBar";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "../../hooks/RestaurantContext";

const PAGE_LIMIT = 24;

const ModifyMedia = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId } = useRestaurant();
  const [open, setOpen] = useState(false);
  const [mediaId, setMediaId] = useState("");
  const mediaItems = useSelector(selectAllMedias);
  const currentPage = useSelector(selectCurrentPage);
  const totalCount = useSelector(selectTotalCount);
  const status = useSelector(getMediaStatus);
  const error = useSelector(getMediasError);
  const success = useSelector(getMediaSuccess);
  const totalPages = Math.ceil(totalCount / PAGE_LIMIT);

  const fetchMedia = useCallback(
    (page = 1) => {
      dispatch(getMedia({ page, limit: PAGE_LIMIT, replace: true, restaurantId }));
    },
    [dispatch, restaurantId]
  );

  useEffect(() => {
    fetchMedia(1);
  }, [fetchMedia]);

  const handleOpen = (id) => {
    setOpen(true);
    setMediaId(id);
  };

  const handleDelete = () => {
    const item = mediaItems.find((m) => m._id === mediaId);
    dispatch(
      deleteMedia({
        id: mediaId,
        restaurantId: restaurantId || item?.restaurantId,
      })
    );
    setOpen(false);
  };

  const handlePageChange = (_event, value) => {
    fetchMedia(value);
  };

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success || t("media.deleteSuccess"));
      dispatch(updateStatus());
      // Deleting the last item on a page beyond the first should fall back
      // to the previous page instead of showing an empty page.
      if (mediaItems.length === 0 && currentPage > 1) {
        fetchMedia(currentPage - 1);
      }
    } else if (status === "deleteError") {
      toast.error(error);
      dispatch(updateStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="main-application">
      <RestaurantAppBar />
      <Box m="20px" className="main-application">
        <BackButton />
        <Header
          title={t("headers.editMedia")}
          subtitle={t("headers.editMediaSub")}
        />
        <main>
          <Box sx={{ p: 2 }}>
            {status === "loadingMedia" ? (
              <Loading />
            ) : mediaItems.length === 0 ? (
              <NoData />
            ) : (
              <Grid container spacing={2}>
                {mediaItems.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                    <Card sx={{ position: "relative" }}>
                      <CardMedia
                        component={
                          item.type === "carousel" && item.url?.endsWith(".mp4")
                            ? "video"
                            : "img"
                        }
                        image={`${process.env.REACT_APP_MEDIA_URL}/${item.url}`}
                        src={
                          item.type === "carousel" && item.url?.endsWith(".mp4")
                            ? `${process.env.REACT_APP_MEDIA_URL}/${item.url}`
                            : undefined
                        }
                        alt={item.filename || "media"}
                        sx={{ height: 300, objectFit: "contain" }}
                        controls={item.type === "carousel" && item.url?.endsWith(".mp4")}
                      />
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 5,
                          backgroundColor: "rgba(255,255,255,0.8)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,1)",
                          },
                        }}
                        onClick={() => handleOpen(item._id)}
                        size="small"
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage || 1}
                onChange={handlePageChange}
                disabled={status === "loadingMedia"}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          <AlertDialog
            handleClose={() => setOpen(false)}
            open={open}
            message={t("media.deleteConfirm")}
            cardId={mediaId}
            deleteData={handleDelete}
          />
        </main>
      </Box>
    </div>
  );
};

export default ModifyMedia;
